import { useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  FileSearch,
  Loader2,
  BookOpen,
} from "lucide-react";
import { Paciente } from "../../../types";
import { AnaliseIA } from "../types";
import { theme } from "../../../shared/styles/theme";
import { coletarDadosCompletos } from "../services/coletarDadosPaciente";
import { descreverProveniencia } from "../services/provenienciaGeracao";
import {
  derivarProveniencia,
  GrupoProveniencia,
} from "../services/proveniencia";

// B229 (AI_147) — PAINEL "Proveniência desta análise" (Caminho 1).
// Seção anexada abaixo da análise geral: mostra os FATOS do prontuário em que a
// análise se apoia (labs + marcadores da coleta mais recente), com data e origem.
// Determinístico, sem IA e sem tocar a geração — só transparência (risco zero).
// Carrega sob demanda (ao expandir) a MESMA coleta que a IA usou.
// B229 fase 2 / B328 (AI_148): mostra também as DIRETRIZES recuperadas do RAG
// nesta análise (`analise.documentosRag`), quando presentes.
export function PainelProveniencia({
  paciente,
  analise,
}: {
  paciente: Paciente;
  analise?: AnaliseIA;
}) {
  const [aberto, setAberto] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [grupos, setGrupos] = useState<GrupoProveniencia[] | null>(null);
  const [erro, setErro] = useState(false);

  const abrir = async () => {
    if (aberto) {
      setAberto(false);
      return;
    }
    setAberto(true);
    if (grupos !== null || carregando) return; // já carregado (cache na sessão)
    setCarregando(true);
    setErro(false);
    try {
      const dados = await coletarDadosCompletos(paciente);
      setGrupos(derivarProveniencia(dados));
    } catch {
      setErro(true);
    } finally {
      setCarregando(false);
    }
  };

  const documentos = analise?.documentosRag ?? [];
  // B388 — linha "gerada por" (ausente nas análises antigas).
  const geracao = analise?.proveniencia
    ? descreverProveniencia(analise.proveniencia)
    : null;

  return (
    <div
      style={{
        marginTop: "16px",
        border: `1px solid ${theme.gray[200]}`,
        borderRadius: theme.borderRadius.lg,
        background: theme.gray[50],
        overflow: "hidden",
      }}
    >
      <button
        onClick={abrir}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          padding: "12px 16px",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          fontFamily: theme.fonts.sans,
          fontSize: "14px",
          fontWeight: 600,
          color: theme.gray[700],
          textAlign: "left",
        }}
      >
        {aberto ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <FileSearch size={16} color={theme.primary[600]} />
        Proveniência desta análise
        <span
          style={{
            fontWeight: 400,
            fontSize: "12px",
            color: theme.gray[500],
          }}
        >
          — fatos do prontuário em que ela se apoia
        </span>
      </button>

      {aberto && (
        <div style={{ padding: "0 16px 16px 16px" }}>
          {/* B388 (AI_156) — modelo/esforço/versão que gerou esta análise. */}
          {geracao && (
            <div
              style={{
                fontFamily: theme.fonts.sans,
                fontSize: "11px",
                color: theme.gray[500],
                marginBottom: "10px",
              }}
            >
              Gerada por {geracao}
            </div>
          )}
          {/* B229 fase 2 / B328 — diretrizes recuperadas do RAG nesta análise.
              Síncrono (já em memória); some em análises antigas (sem o campo). */}
          {documentos.length > 0 && (
            <div style={{ marginBottom: "4px" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  marginBottom: "6px",
                }}
              >
                <BookOpen size={14} color={theme.primary[600]} />
                <span
                  style={{
                    fontFamily: theme.fonts.sans,
                    fontSize: "13px",
                    fontWeight: 700,
                    color: theme.gray[800],
                  }}
                >
                  Diretrizes consultadas
                </span>
                <span
                  style={{
                    fontFamily: theme.fonts.sans,
                    fontSize: "11px",
                    color: theme.gray[500],
                  }}
                >
                  {documentos.length} documento
                  {documentos.length > 1 ? "s" : ""} recuperado
                  {documentos.length > 1 ? "s" : ""} pelo RAG
                </span>
              </div>
              <div
                style={{ display: "flex", flexDirection: "column", gap: "6px" }}
              >
                {documentos.map((d, di) => (
                  <div
                    key={di}
                    style={{
                      background: "white",
                      border: `1px solid ${theme.gray[200]}`,
                      borderRadius: theme.borderRadius.md,
                      padding: "8px 10px",
                    }}
                  >
                    <div
                      style={{
                        fontFamily: theme.fonts.sans,
                        fontSize: "12px",
                        fontWeight: 600,
                        color: theme.gray[800],
                        lineHeight: 1.35,
                      }}
                    >
                      {d.titulo}
                    </div>
                    <div
                      style={{
                        fontFamily: theme.fonts.sans,
                        fontSize: "11px",
                        color: theme.gray[500],
                        marginTop: "2px",
                      }}
                    >
                      {[
                        d.organizacao,
                        d.ano ? String(d.ano) : "",
                        `relevância ${d.relevancia}%`,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                  </div>
                ))}
              </div>
              <div
                style={{
                  height: "1px",
                  background: theme.gray[200],
                  margin: "14px 0",
                }}
              />
            </div>
          )}

          {carregando && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                color: theme.gray[500],
                fontFamily: theme.fonts.sans,
                fontSize: "13px",
                padding: "8px 0",
              }}
            >
              <Loader2 size={14} /> Levantando os fatos…
            </div>
          )}

          {erro && (
            <p
              style={{
                color: theme.gray[500],
                fontFamily: theme.fonts.sans,
                fontSize: "13px",
              }}
            >
              Não foi possível levantar os fatos agora. Tente reabrir.
            </p>
          )}

          {!carregando && !erro && grupos && grupos.length === 0 && (
            <p
              style={{
                color: theme.gray[500],
                fontFamily: theme.fonts.sans,
                fontSize: "13px",
              }}
            >
              Sem fatos estruturados de laboratório ou marcadores para exibir.
            </p>
          )}

          {!carregando && !erro && grupos && grupos.length > 0 && (
            <>
              {grupos.map((g, gi) => (
                <div key={gi} style={{ marginTop: gi === 0 ? "4px" : "14px" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      gap: "8px",
                      marginBottom: "6px",
                    }}
                  >
                    <span
                      style={{
                        fontFamily: theme.fonts.sans,
                        fontSize: "13px",
                        fontWeight: 700,
                        color: theme.gray[800],
                      }}
                    >
                      {g.titulo}
                    </span>
                    <span
                      style={{
                        fontFamily: theme.fonts.sans,
                        fontSize: "11px",
                        color: theme.gray[500],
                      }}
                    >
                      {g.aba}
                      {g.data ? ` · ${g.data}` : ""}
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "6px",
                    }}
                  >
                    {g.itens.map((it, ii) => (
                      <span
                        key={ii}
                        style={{
                          fontFamily: theme.fonts.sans,
                          fontSize: "12px",
                          color: theme.gray[700],
                          background: "white",
                          border: `1px solid ${theme.gray[200]}`,
                          borderRadius: "999px",
                          padding: "2px 10px",
                        }}
                      >
                        {it.rotulo ? (
                          <>
                            <strong style={{ fontWeight: 600 }}>
                              {it.rotulo}:
                            </strong>{" "}
                          </>
                        ) : null}
                        {it.valor}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
              <p
                style={{
                  marginTop: "14px",
                  marginBottom: 0,
                  fontFamily: theme.fonts.sans,
                  fontSize: "11px",
                  color: theme.gray[400],
                  fontStyle: "italic",
                }}
              >
                Principais fatos do prontuário lidos pela IA (labs e marcadores da
                coleta mais recente; medicações em uso; 3 consultas mais recentes)
                {documentos.length > 0
                  ? ", com as diretrizes recuperadas listadas acima."
                  : "."}
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
